import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Box,
  IconButton,
  Dialog,
  DialogContent,
  Button,
  TextField,
  Snackbar,
  Card,
  CardHeader,
  CardContent,
} from "@mui/material";
import GridToolbarNoExport from "../../common/GridToolbarNoExport";
import AppDataGrid from "../../common/AppDataGrid";
import EditIcon from "@mui/icons-material/Edit";
import { doc, getDocs, updateDoc, collection } from "firebase/firestore";
import { db } from "../../../config/firebase";
import CcpAdminBreadCrumbs from "../../Layout/CcpAdminBreadCrumbs";
import { faBarChart } from "@fortawesome/free-solid-svg-icons";
import ModalDialogTitle from "../../common/ModalDialogTitle";
import ModalActions from "../../common/ModalActions";

const CcpAdminPortalContent = () => {
  const [rows, setRows] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const pageName = "Portal Content";
  const icon = faBarChart;

  useEffect(() => {
    const fetchData = async () => {
      const querySnapshot = await getDocs(collection(db, "pages"));
      const data = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort data by 'name' in ascending order
      data.sort((a, b) => a.name.localeCompare(b.name));

      setRows(data);
    };
    fetchData();
  }, []);

  const handleOpenDialog = (row) => {
    setSelectedRow(row);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRow(null);
  };

  const handleUpdate = async () => {
    if (selectedRow) {
      setLoading(true);
      try {
        const docRef = doc(db, "pages", selectedRow.id);
        await updateDoc(docRef, {
          name: selectedRow.name,
          content: selectedRow.content,
        });
        // Refresh the table data
        const querySnapshot = await getDocs(collection(db, "pages"));
        const updatedData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        updatedData.sort((a, b) => a.name.localeCompare(b.name));
        setRows(updatedData);
        setSnackbarMessage("Content has been updated");
        setSnackbarOpen(true);
      } catch (error) {
        console.error("Error updating document:", error);
        setSnackbarMessage("Error updating content");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
        handleCloseDialog();
      }
    }
  };

  const handleFieldChange = (field, value) => {
    setSelectedRow((prev) => ({ ...prev, [field]: value }));
  };

  const columns = [
    { field: "name", headerName: "Name", flex: 1 },
    { field: "content", headerName: "Content", flex: 2 },
    {
      field: "edit",
      headerName: "Edit",
      width: 100,
      renderCell: (params) => (
        <IconButton
          color="primary"
          onClick={() => handleOpenDialog(params.row)}
        >
          <EditIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Container sx={{ marginTop: "100px" }}>
      <CcpAdminBreadCrumbs pageName={pageName} icon={icon} />
      <Box sx={{ flexGrow: 1, marginTop: "10px" }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Portal Content"
                sx={{ backgroundColor: "#AC1A2F", color: "white" }}
              />
              <CardContent>
                <AppDataGrid
                  rows={rows}
                  columns={columns}
                  slots={{ toolbar: GridToolbarNoExport }}
                  autoHeight
                  disableColumnMenu
                  disableSelectionOnClick
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <ModalDialogTitle onClose={handleCloseDialog}>
          Edit Content
        </ModalDialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Name"
            fullWidth
            value={selectedRow?.name || ""}
            onChange={(e) => handleFieldChange("name", e.target.value)}
          />
          <TextField
            margin="dense"
            label="Content"
            fullWidth
            multiline
            rows={4}
            value={selectedRow?.content || ""}
            onChange={(e) => handleFieldChange("content", e.target.value)}
          />
        </DialogContent>
        <ModalActions
          onSave={handleUpdate}
          saving={loading}
          saveLabel="Update Content"
        />
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        ContentProps={{
          sx: { bgcolor: "green", color: "white" },
        }}
        action={
          <Button color="inherit" onClick={() => setSnackbarOpen(false)}>
            Close
          </Button>
        }
      />
    </Container>
  );
};

export default CcpAdminPortalContent;
